import type { NavigateFunction } from "react-router-dom";
import type { Borrower } from "@/types/borrower";
import {
  warmAdminAppDetailTab,
  writeAdminAppDetailTab,
  writeBackgroundCheckChip,
} from "@/pages/admin/adminAppDetailTab";

export type ApplicationRef = Pick<Borrower, "id" | "app_status">;

export function applicationPath(borrowerId: number): string {
  return `/applications/${borrowerId}`;
}

/** Warm the admin detail tab, then navigate (or open a new tab). */
export function openApplication(
  navigate: NavigateFunction,
  borrower: ApplicationRef,
  opts?: { newTab?: boolean }
): void {
  warmAdminAppDetailTab(borrower.id, borrower.app_status);
  const path = applicationPath(borrower.id);
  if (opts?.newTab) {
    window.open(path, "_blank");
    return;
  }
  navigate(path);
}

/** Force the Application tab, then navigate in-place. */
export function openApplicationForEdit(navigate: NavigateFunction, borrower: ApplicationRef): void {
  writeAdminAppDetailTab(borrower.id, "application", borrower.app_status);
  navigate(applicationPath(borrower.id));
}

/** Open the application on its Default History tab (the admin and broker detail pages both read it). */
export function openApplicationDefaultHistory(
  navigate: NavigateFunction,
  borrower: ApplicationRef
): void {
  writeAdminAppDetailTab(borrower.id, "default-history", borrower.app_status);
  writeBackgroundCheckChip(borrower.id, "history");
  localStorage.setItem(
    "broker_app_detail_active_tab_v2",
    JSON.stringify({ id: String(borrower.id), tab: "default-history", status: borrower.app_status })
  );
  navigate(applicationPath(borrower.id));
}

/** Ctrl/Cmd-click or middle-click — same as a normal browser link. */
export function isOpenInNewTabClick(e: {
  ctrlKey: boolean;
  metaKey: boolean;
  button: number;
}): boolean {
  return e.ctrlKey || e.metaKey || e.button === 1;
}

/**
 * Shared activate handler for table rows / workflow cards.
 * Modifier/middle-click opens a new tab; otherwise runs `onSameTab`.
 */
export function handleApplicationActivate(
  e: { ctrlKey: boolean; metaKey: boolean; button: number; preventDefault: () => void },
  navigate: NavigateFunction,
  borrower: ApplicationRef,
  onSameTab: () => void
): void {
  if (isOpenInNewTabClick(e)) {
    e.preventDefault();
    openApplication(navigate, borrower, { newTab: true });
    return;
  }
  onSameTab();
}
